// src/pages/TextTools/AskDoc.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import ChatView from '../../components/AskDoc/ChatView'; 
import { startDocumentUpload, getDocumentUploadStatus, askDocument, deleteDocumentSession } from '../../services/chatApi'; // Adjust path
import { UploadCloud, MessageSquare, Loader, AlertCircle, FileUp, Copy, Check, XCircle, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { AskDoc_PROMPTS } from './askDocPrompts';
// import SubscribePrompt from '../../components/ToolAccess/SubscribePrompt';
// import PurchaseCreditsPrompt from '../../components/ToolAccess/PurchaseCreditsPrompt';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};


const AskDoc = () => {
    const { t, i18n } = useTranslation();
    const { user, isAuthenticated, setCredits } = useAuth();
    

    // UI & State
    const [file, setFile] = useState(null);
    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState('');
    const [fileName, setFileName] = useState('');
    const [error, setError] = useState(null);
    
    // Status State
    const [isUploading, setIsUploading] = useState(false);
    const [isAnswering, setIsAnswering] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    // Session State
    const [collectionName, setCollectionName] = useState(null);
    const collectionNameRef = useRef(collectionName); // Ref to use in cleanup
    const pollingIntervalRef = useRef(null);

    console.log('🎨 RENDER - collectionName:', collectionName, 'messages:', messages.length);

    // Update ref whenever collectionName changes
    useEffect(() => {
        console.log('⚡ useEffect triggered - collectionName changed to:', collectionName);
        collectionNameRef.current = collectionName;
    }, [collectionName]);

    // Cleanup effect to delete session on unmount
    useEffect(() => {
        return () => {
            if (collectionNameRef.current) {
                console.log(`Cleaning up document session: ${collectionNameRef.current}`);
                deleteDocumentSession(collectionNameRef.current);
            }
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setError(null);
            setFileName(selectedFile.name);
        }
    };

    const handleClear = () => {
        setFile(null);
        setFileName('');
        setError(null);
        // Reset the file input visually
        document.getElementById('file-upload').value = '';
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
    };

    const pollForUploadStatus = (jobId) => {
        pollingIntervalRef.current = setInterval(async () => {
            try {
                const result = await getDocumentUploadStatus(jobId);
                if (result) {
                    if (result.status === 'SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        const collectionNameFromApi = result.collection_name;
                        
                        const uploadMessage = {
                            role: 'assistant',
                            content: `${t('askDoc.uploadSuccess')} **${result.original_filename}**. ${t('askDoc.readyPrompt')}`
                        };

                        setCollectionName(collectionNameFromApi);
                        setMessages([uploadMessage]);
                        setIsUploading(false);
                        setFile(null); // Clear the selected file
                    } else if (result.status === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(result.error_message || t('askDoc.errorUpload'));
                        setIsUploading(false);
                    }
                    // If PENDING, do nothing, the interval will poll again
                }
            } catch (err) {
                clearInterval(pollingIntervalRef.current);
                setError(err.message);
                setIsUploading(false);
            }
        }, 3000); // Poll every 3 seconds
    };

    const handleUpload = async () => {
        console.log('🚀 handleUpload STARTED'); 
        if (!file) {
            console.log('⚠️ No file selected');
            setError(t('askDoc.errorNoFile'));
            return;
        }

        // If there's an old session, clean it up first
        if (collectionName) {
            console.log('🧹 Cleaning up old session:', collectionName);
            await deleteDocumentSession(collectionName);
            
        }

        setIsUploading(true);
        setError(null);
        setMessages([]);
        setCollectionName(null);

        try {
            // 1. Start the job
            const response = await startDocumentUpload(file);
            
            // 2. Begin polling
            if (response.job_id) {
                pollForUploadStatus(response.job_id);
            } else {
                throw new Error('Backend did not return a job_id');
            }
        } catch (err) {
            setError(err.message || t('askDoc.errorUpload'));
            setIsUploading(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!userInput.trim() || isAnswering) return;

        const question = userInput.trim();
        const newMessages = [...messages, { role: 'user', content: question }];
        setMessages(newMessages);
        setUserInput('');
        setIsAnswering(true);

        try {
            // Format chat history for the API: [("q1", "a1"), ("q2", "a2")]
            let historyForApi = messages
                .filter(m => m.role !== 'system')
                .reduce((acc, msg, i) => {
                    if (msg.role === 'user') {
                        const nextMsg = messages[i + 1];
                        if (nextMsg && nextMsg.role === 'assistant') {
                            acc.push([msg.content, nextMsg.content]);
                        }
                    }
                    return acc;
                }, []);
            let systemPromptForApi = null;

            const language = i18n.language.split('-')[0];
            const prompts = AskDoc_PROMPTS[language] || AskDoc_PROMPTS['en'];
            systemPromptForApi = prompts.system_prompt;
             // It's no longer the first question
           


            const response = await askDocument(collectionName, question, historyForApi, systemPromptForApi);
            
            setMessages([...newMessages, { 
                role: 'assistant', 
                content: response.answer,
                sources: response.sources
            }]);

            if (response.new_credits !== undefined) {
                setCredits(response.new_credits);
            }

        } catch (err) {
            setError(err.message || t('askDoc.errorAnswering'));
        } finally {
            setIsAnswering(false);
        }
    };

    
    const handleCopy = (content, index) => {
        if (content) {
            navigator.clipboard.writeText(content);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000); // Reset after 2s
        }
    };

    // UI Components
    const UploadView = () => {
        console.log('📄 Rendering UploadView');
        return (
        <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold mb-2">{t('askDoc.title')}</h2>
            <p className="mt-2 text-brand-dark">{t('askDoc.description')}</p>
            <div className="mt-8">
                <label htmlFor="file-upload" className="block text-sm font-medium mb-2 text-brand-dark">
                    {t('askDoc.uploadLabel')}
                </label>
                <div className="flex items-center p-4 border-2 border-dashed border-slate-400 rounded-md bg-slate-50">
                    <FileUp className="h-8 w-8 text-slate-500" />
                    <label htmlFor="file-upload" className="ml-4 text-sm text-brand-orange font-semibold cursor-pointer">
                        {t('askDoc.selectFile')}
                        <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} accept=".pdf,.txt,.docx" />
                    </label>
                    {fileName && (
                           <div className="ml-auto flex items-center gap-2 text-sm text-slate-700">
                                <span className="truncate max-w-[200px] md:max-w-xs">{fileName}</span>
                                <button onClick={handleClear} title={t('askDoc.clearFile')}>
                                    <XCircle className="h-5 w-5 text-red-500 hover:text-red-700" />
                                </button>
                           </div>
                        )}
                    
                    {error && <p className="mt-4 text-red-500 flex items-center"><AlertCircle size={16} className="mr-2"/>{error}</p>}
                </div>
                <p className="text-xs text-slate-500 mt-1">{t('askDoc.fileTypes')}</p>
            </div>

            <div className="flex items-center space-x-4 mt-6">
                <button
                    onClick={handleUpload}
                    disabled={!file || isUploading}
                    className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed"
                >
                    {isUploading ? <Loader className="animate-spin" /> : t('askDoc.uploadButton')}
                </button>
            </div>
                
        </div>
    );
    };
    
    console.log('🔀 Conditional render check - collectionName:', collectionName, 'should render ChatView:', !!collectionName);

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-orange bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : collectionName ? (
                // User has access and has uploaded a document
                <ChatView
                    messages={messages}
                    userInput={userInput}
                    setUserInput={setUserInput}
                    isAnswering={isAnswering}
                    handleSendMessage={handleSendMessage}
		    handleCopy={handleCopy}
                    copiedIndex={copiedIndex}
                />
            ) : (
                // User has access but needs to upload a document
                <UploadView />
            )}
        </div>
    );

};

export default AskDoc;
